/* ═══════════════════════════════════════════════════════════════
   groupOptions — merging a caller's `groupOptions=` prop with the
   grouping DataTable derives for every column on its own.

   A passed option whose `key` matches a COLUMN replaces that column's
   derived grouping (same meaning, one entry — e.g. "group CSI # by
   Division" instead of by the raw string). A passed option whose key
   matches NO column is a grouping with no column of its own (there is
   no "Division" column) and must still reach the menu: it is appended
   after the column-derived options, in the order it was passed, never
   dropped.

   Zero imports on purpose — provable without React or a DOM.
   ═══════════════════════════════════════════════════════════════ */

export interface GroupOptionLike {
  key: string;
  label: string;
}

export interface ColumnLike {
  key: string;
  label: string;
}

/**
 * One entry per column (overridden by a passed option of the same key),
 * followed by whatever passed options matched no column — in the order
 * they were passed, each key appearing at most once.
 */
export function mergeGroupOptions<G extends GroupOptionLike, C extends ColumnLike>(
  columns: C[],
  passed: G[],
  deriveDefault: (col: C) => G
): G[] {
  // A repeated key keeps its LAST passed value — same rule a plain object
  // merge would give it.
  const overrides = new Map(passed.map((g) => [g.key, g]));
  const columnKeys = new Set(columns.map((c) => c.key));
  const fromColumns = columns.map((col) => overrides.get(col.key) || deriveDefault(col));
  // Unmatched keys, each once, in the order they first appeared.
  const extraKeys: string[] = [];
  const seen = new Set<string>();
  for (const g of passed) {
    if (columnKeys.has(g.key) || seen.has(g.key)) continue;
    seen.add(g.key);
    extraKeys.push(g.key);
  }
  const extra = extraKeys.map((key) => overrides.get(key)!);
  return [...fromColumns, ...extra];
}
